import { CONFIG, STEP } from "../engine/config";

/**
 * TutorialSim — the scripted 4-beat interactive tutorial (spec §8.2).
 * Not scored, not seeded, never touches bests or achievements. Movement
 * matches Classic exactly so learned control transfers 1:1.
 */

export type TutorialBeat = 1 | 2 | 3 | 4;

export interface TutState {
  beat: TutorialBeat;
  copy: string;
}

export class TutorialSim {
  px: number = CONFIG.playerStartX;
  py: number = CONFIG.playerStartY;
  tx: number = CONFIG.playerStartX;
  ty: number = CONFIG.playerStartY;
  touching = false;
  t = 0;
  acc = 0;

  beat: TutorialBeat = 1;
  shard: { x: number; y: number } | null = { x: 50, y: 325 }; // 130 units left of start
  route: { x: number; y: number }[] = [];
  private recording: { x: number; y: number }[] = [{ x: CONFIG.playerStartX, y: CONFIG.playerStartY }];
  private nextSampleAt = 1 / 60;
  routeDur = 0;
  echoT = 0;
  pulseT = 0;
  collidable = false;
  justHit = 0;
  justPicked = 0;

  events: ("pickup" | "beat2" | "beat3" | "beat4" | "hit")[] = [];

  setInput(tx: number, ty: number, touching: boolean): void {
    this.tx = Math.max(CONFIG.boundsMinX, Math.min(CONFIG.boundsMaxX, tx));
    this.ty = Math.max(CONFIG.boundsMinY, Math.min(CONFIG.boundsMaxY, ty));
    this.touching = touching;
  }

  update(dt: number): void {
    this.acc += Math.min(dt, 0.1);
    while (this.acc >= STEP) {
      this.acc -= STEP;
      this.step();
    }
    if (this.justHit > 0) this.justHit -= dt;
    if (this.justPicked > 0) this.justPicked -= dt;
  }

  private step(): void {
    this.t += STEP;
    if (this.touching) {
      const dx = this.tx - this.px;
      const dy = this.ty - this.py;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 1e-9) {
        const move = Math.min(dist, CONFIG.touchSpeed * STEP);
        this.px += (dx / dist) * move;
        this.py += (dy / dist) * move;
      }
    }
    this.px = Math.max(CONFIG.boundsMinX, Math.min(CONFIG.boundsMaxX, this.px));
    this.py = Math.max(CONFIG.boundsMinY, Math.min(CONFIG.boundsMaxY, this.py));
    while (this.t >= this.nextSampleAt) {
      this.recording.push({ x: this.px, y: this.py });
      this.nextSampleAt += 1 / 60;
    }

    if (this.beat === 1) {
      if (this.shard && this.near(this.shard, CONFIG.pickupDistance)) {
        this.events.push("pickup");
        this.route = [...this.recording];
        this.routeDur = this.t;
        this.shard = null;
        this.beat = 2;
        this.echoT = 0;
        this.events.push("beat2");
      }
    } else if (this.beat === 2) {
      this.echoT += STEP;
      if (this.echoT >= this.routeDur + 0.8) {
        this.beat = 3;
        this.echoT = 0;
        this.pulseT = 0;
        this.collidable = false;
        this.shard = { x: 310, y: 325 };
        this.events.push("beat3");
      }
    } else if (this.beat === 3) {
      this.echoT += STEP;
      this.pulseT += STEP;
      if (this.pulseT > 0.9) this.collidable = true;
      const ep = this.echoPos();
      if (this.collidable && ep && this.near(ep, CONFIG.collisionThreshold)) {
        // restore the beat immediately without losing a life (spec §8.2 beat 3)
        this.events.push("hit");
        this.justHit = 0.4;
        this.echoT = 0;
        this.pulseT = 0;
        this.collidable = false;
      }
      if (this.shard && this.near(this.shard, CONFIG.pickupDistance)) {
        this.events.push("pickup");
        this.shard = null;
        this.beat = 4;
        this.justPicked = 0.3;
        this.events.push("beat4");
      }
    }
  }

  private near(p: { x: number; y: number }, dist: number): boolean {
    const dx = this.px - p.x;
    const dy = this.py - p.y;
    return dx * dx + dy * dy < dist * dist;
  }

  echoPos(): { x: number; y: number } | null {
    if (this.route.length < 2) return null;
    const tt = this.echoT % (this.routeDur + 0.6);
    const idx = Math.min(tt * 60, this.route.length - 1);
    const i = Math.floor(idx);
    const a = this.route[i];
    const b = this.route[Math.min(i + 1, this.route.length - 1)];
    const f = idx - i;
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
  }

  copy(): string {
    switch (this.beat) {
      case 1:
        return "DRAG TO THE LIGHT";
      case 2:
        return "YOUR PATH COMES BACK";
      case 3:
        return "MOVE WHERE YOU WEREN'T";
      default:
        return "";
    }
  }
}
